import React, { useState } from 'react';
import { MapPin, Clock, Info } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { useSiteConfig, resolveImageUrl } from '../../context/SiteConfigContext';
import TourModal from './TourModal';

// Per-circuit color identity kept for tint overlay + badge accent
const PALETTE = {
  nord:     { tint: '30, 94, 70',   accent: '#a8d5c1', text: '#f5f1e8' },
  sud:      { tint: '28, 138, 156', accent: '#a8e0e8', text: '#f5f1e8' },
  plage:    { tint: '181, 106, 36', accent: '#ffcf95', text: '#fff8ee' },
  banana:   { tint: '201, 154, 30', accent: '#fff0aa', text: '#fff8ee' },
  transfert:{ tint: '43, 74, 114',  accent: '#b8ccec', text: '#f5f1e8' },
};

const CircuitCard = ({ circuit, onOpen }) => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const p = PALETTE[circuit.color] || PALETTE.nord;
  const content = circuit.translations?.[lang] || {};
  const csection = config?.circuitsSection?.[lang] || { details: 'Details', per: '' };

  if (!content.title) return null;

  // Per-language image override (translations[lang].image) or default circuit.image
  const rawImage = content.image || circuit.image;
  const img = resolveImageUrl(rawImage);

  const overlay = `linear-gradient(180deg, rgba(${p.tint},0.78) 0%, rgba(0,0,0,0.75) 45%, rgba(0,0,0,0.92) 100%)`;

  return (
    <article
      onClick={() => onOpen(circuit)}
      className="group relative overflow-hidden rounded-lg shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
      style={{ color: p.text, minHeight: '460px' }}
    >
      {/* Background image */}
      {img && (
        <img
          src={img}
          alt={content.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
      )}
      {/* Overlay for readability */}
      <div className="absolute inset-0" style={{ background: overlay }} />

      {/* Content */}
      <div className="relative p-7 md:p-9 h-full flex flex-col" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase" style={{ color: p.accent }}>{content.badge}</p>
            <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tight">{content.title}</h3>
            <p className="mt-1 text-[13px] opacity-90">{content.subtitle}</p>
          </div>
          <span className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-white/20 group-hover:bg-white/30 backdrop-blur-sm border border-white/20 transition-colors">
            <Info size={15} />
          </span>
        </div>

        <ul className="mt-6 space-y-1.5 flex-1">
          {(content.stops || []).slice(0, 5).map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px]">
              <MapPin size={13} className="mt-1 shrink-0" style={{ color: p.accent }} />
              <span className="opacity-95">{s}</span>
            </li>
          ))}
          {(content.stops || []).length > 5 && (
            <li className="text-[12px] opacity-70 pl-5">+ {(content.stops || []).length - 5}…</li>
          )}
        </ul>

        <div className="mt-5 flex items-center justify-between text-[12px] font-mono opacity-85">
          {circuit.start && circuit.start !== '—' ? (
            <>
              <span>{circuit.start}</span>
              <div className="flex-1 mx-3 h-px border-t border-dashed border-white/30" />
              <span>{circuit.end}</span>
            </>
          ) : (
            <span className="inline-flex items-center gap-2"><Clock size={12} /> {circuit.duration}</span>
          )}
        </div>

        <div className="mt-5 flex items-end justify-between pt-4 border-t border-white/25">
          <div>
            <span className="text-2xl font-serif">{circuit.price}€</span>
            <span className="ml-1 text-[12px] opacity-80">{csection.per}</span>
          </div>
          <span className="text-[12.5px] font-medium inline-flex items-center gap-1 rounded-md bg-white/15 group-hover:bg-white/25 backdrop-blur-sm px-3 py-1.5 border border-white/25 transition-colors">
            {csection.details} →
          </span>
        </div>
      </div>
    </article>
  );
};

const Circuits = () => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const [active, setActive] = useState(null);

  if (!config) return null;
  const cs = config.circuitsSection?.[lang] || {};
  const circuits = (config.circuits || []).filter((c) => (c.availableLangs || []).includes(lang) && c.translations?.[lang]?.title);

  return (
    <section id="circuits" className="bg-[#f5f1e8] text-[#0e2a24]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 border-t border-[#0e2a24]/10">
        <p className="text-[11px] tracking-[0.25em] uppercase text-[#0e2a24]/50">{cs.overline}</p>
        <h2 className="mt-4 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl">{cs.title}</h2>
        <p className="mt-4 text-[15px] text-[#0e2a24]/70 max-w-2xl">{cs.lead}</p>

        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {circuits.map((c) => (<CircuitCard key={c.id} circuit={c} onOpen={setActive} />))}
        </div>
      </div>

      {active && <TourModal circuit={active} onClose={() => setActive(null)} />}
    </section>
  );
};

export default Circuits;
